import type { OnboardingAnswers } from "../../types/onboarding";

// Screen id → the answer field that screen owns. Only screens whose field has
// downstream dependents are listed.
export const SCREEN_FIELD: Record<string, string> = {
	setupDetails: "gender",
	status: "fertilityStatus",
	role: "fertilityRole",
	journey: "fertilityJourneyTypes",
};

const CYCLE_FIELDS = [
	"cycleNotRelevant",
	"cycleLastPeriodStart",
	"cyclePeriodLengthDays",
	"cycleLengthDays",
	"cycleRegular",
];

const ARRAY_FIELDS = new Set([
	"fertilityJourneyTypes",
	"journeyCheckedStepIds",
]);

// Changed field → fields whose value is now stale and must be cleared. Order is
// upstream→downstream; tests assert exact order.
//
// NOTE: donorType is deliberately NOT a dependent. It is co-owned by the status
// screen (set via the donor micro-question / derived from gender on that same
// screen and persisted by saveScreen("status")), not a downstream screen.
// Including it would null the donorType the user just picked on a TTC→Donor
// Back-edit (saveScreen persists it, then the cascade clear immediately wipes it).
// A leftover donorType after leaving donor status is inert (only read when
// fertilityStatus === egg_sperm_donor).
export const DEPENDENTS: Record<string, string[]> = {
	gender: [
		"fertilityRole",
		"fertilityJourneyTypes",
		"journeyCheckedStepIds",
		...CYCLE_FIELDS,
	],
	fertilityStatus: [
		"fertilityRole",
		"fertilityJourneyTypes",
		"journeyCheckedStepIds",
		...CYCLE_FIELDS,
	],
	fertilityRole: [
		"fertilityJourneyTypes",
		"journeyCheckedStepIds",
		...CYCLE_FIELDS,
	],
	// Journey type does NOT clear cycle: the cycle (last period, length,
	// regularity) is the user's menstrual data, independent of which fertility
	// journey they're on. Changing journey only invalidates the step selection.
	// (When the new journey makes the cycle screen irrelevant, shouldSkipCycle
	// hides it; any stored cycle data is then inert, like a leftover donorType.)
	fertilityJourneyTypes: ["journeyCheckedStepIds"],
};

export function dependentsToClear(field: string): string[] {
	return DEPENDENTS[field] ?? [];
}

type AnswerValue = string | string[] | boolean | undefined;

// True when the value meaningfully changed. Arrays compare order-insensitively
// (journey selection order is irrelevant).
export function fieldChanged(prev: AnswerValue, cur: AnswerValue): boolean {
	if (Array.isArray(prev) || Array.isArray(cur)) {
		const a = Array.isArray(prev) ? [...prev].sort() : [];
		const b = Array.isArray(cur) ? [...cur].sort() : [];
		if (a.length !== b.length) return true;
		return a.some((v, i) => v !== b[i]);
	}
	return prev !== cur;
}

// True when a value was already committed (non-empty). A change from a
// previously-set value is a real edit; a change from empty is a first-time set.
// Used to gate persisted (DB) clearing so the first forward walk never fires it.
export function wasCommitted(value: AnswerValue): boolean {
	if (value === undefined || value === "") return false;
	if (Array.isArray(value)) return value.length > 0;
	return true;
}

// Canonical empty value used when wiping a dependent from context.
export function clearedValue(field: string): string[] | undefined {
	return ARRAY_FIELDS.has(field) ? [] : undefined;
}

// Copy of answers with `field`'s dependents cleared in context.
export function clearAnswers(
	answers: OnboardingAnswers,
	field: string,
): OnboardingAnswers {
	const next: OnboardingAnswers = { ...answers };
	for (const dep of dependentsToClear(field)) {
		next[dep] = clearedValue(dep);
	}
	return next;
}
